import React, { useState, useEffect } from 'react';
import { Users, UserPlus, Settings } from 'lucide-react';
import Header from '../../components/ui/Header';
import AgentRoster from './components/AgentRoster';
import AgentProfileEditor from './components/AgentProfileEditor';
import AgentShiftManager from './components/AgentShiftManager';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { bailAgentService } from '../../services/bailAgentService';


const BailAgentManagement = () => {
  const [activeTab, setActiveTab] = useState('roster');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load agents on component mount
  useEffect(() => {
    loadAgents();
  }, []);

  // Load agents from Supabase
  const loadAgents = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let result = await bailAgentService?.getAllAgents();
      
      if (result?.error) {
        setError(result?.error);
        setAgents([]);
      } else {
        setAgents(result?.data || []);
      }
    } catch (err) {
      console.error('Failed to load agents:', err);
      setError('Failed to load agents. Please try again.');
      setAgents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEditAgent = (agent) => {
    setSelectedAgent(agent);
    setIsCreating(false);
    setActiveTab('editor');
  };

  const handleCreateAgent = () => {
    setSelectedAgent(null);
    setIsCreating(true);
    setActiveTab('editor');
  };

  const handleBack = () => {
    setSelectedAgent(null);
    setIsCreating(false);
    setActiveTab('roster');
  };

  const handleAgentSelect = (agent) => {
    setSelectedAgent(agent);
  };

  const handleSaveAgent = async (formData) => {
    setError(null);
    
    try {
      let result;
      
      if (isCreating) {
        result = await bailAgentService?.createAgent(formData);
      } else if (selectedAgent?.id) {
        result = await bailAgentService?.updateAgent(selectedAgent?.id, formData);
      }
      
      if (result?.error) {
        setError(result?.error);
        return false;
      }
      
      // Reload agents and go back to roster
      await loadAgents();
      handleBack();
      return true;
    } catch (err) {
      console.error('Failed to save agent:', err);
      setError('Failed to save agent. Please try again.');
      return false;
    }
  };

  const handleSetOnShift = async (agentId, onShift) => {
    setError(null);
    
    try {
      let result = await bailAgentService?.setAgentOnShift(agentId);
      
      if (result?.error) {
        setError(result?.error);
        return;
      }
      
      // Reload agents to get updated shift statuses
      await loadAgents();
    } catch (err) {
      console.error('Failed to update shift status:', err);
      setError('Failed to update shift status. Please try again.');
    }
  };

  const handleDelete = async (agentId) => {
    if (!window.confirm('Are you sure you want to delete this agent? This action cannot be undone.')) {
      return;
    }
    
    setError(null);
    
    try {
      let result = await bailAgentService?.deleteAgent(agentId);
      
      if (result?.error) {
        setError(result?.error);
        return;
      }
      
      // Reload agents
      await loadAgents();
      
      // Clear selected agent if it was deleted
      if (selectedAgent?.id === agentId) {
        setSelectedAgent(null);
        if (activeTab === 'editor') {
          setActiveTab('roster');
        }
      }
    } catch (err) {
      console.error('Failed to delete agent:', err);
      setError('Failed to delete agent. Please try again.');
    }
  };

  const tabs = [
    { id: 'roster', name: 'Agent Roster', icon: Users },
    { id: 'shifts', name: 'Shift Management', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Bail Agent Management</h1>
              <p className="text-gray-600 mt-1">Manage bail agents, shifts, and homepage display</p>
            </div>
            
            {activeTab === 'roster' && (
              <Button
                onClick={handleCreateAgent}
                className="bg-blue-600 hover:bg-blue-700"
                disabled={loading}
              >
                <UserPlus className="w-5 h-5 mr-2" />
                Add New Agent
              </Button>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 011.414-1.414L11.414 10 8.707 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
              <div className="ml-auto pl-3">
                <button
                  onClick={() => setError(null)}
                  className="text-red-400 hover:text-red-600"
                >
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l1.293 1.293a1 1 0 01-1.414 1.414L10 11.414l-1.293 1.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Tabs */}
        {activeTab !== 'editor' && (
          <div className="bg-white rounded-lg shadow-sm border mb-6">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
                {tabs?.map((tab) => {
                  const Icon = tab?.icon;
                  return (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`${
                        activeTab === tab?.id
                          ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{tab?.name}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>
        )}

        {/* Content */}
        <div>
          {activeTab === 'roster' && (
            <AgentRoster 
              agents={agents}
              selectedAgent={selectedAgent}
              onEditAgent={handleEditAgent}
              onAgentSelect={handleAgentSelect}
              onCreateNew={handleCreateAgent}
              onSetOnShift={handleSetOnShift}
              onDelete={handleDelete}
              loading={loading}
            />
          )}
          
          {activeTab === 'shifts' && (
            <AgentShiftManager 
              agents={agents}
              loading={loading}
              onRefresh={loadAgents}
            />
          )}
          
          {activeTab === 'editor' && (
            <AgentProfileEditor
              agent={selectedAgent}
              isCreating={isCreating}
              isCreatingNew={isCreating}
              onBack={handleBack}
              onSave={handleSaveAgent}
              onCancel={handleBack}
              loading={loading}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default BailAgentManagement;